/**
 * Detection detail (task 44, after task 53).
 *
 * A detection is what was noticed; the alert is what is being worked. So
 * this screen shows the provider evidence per source, the AI panel (which
 * always renders, with the same sentence the notification email uses for
 * a missing summary), the decision trace — every rule the playbook
 * considered and the one that fired — and a link to the alert it raised
 * or joined, if any. Status, triage and the timeline live on the alert.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import {
  ApiError,
  getDetection,
  getDetectionPayload,
  type Detection,
} from '@bluesphere/api-client';
import { PillButton, SEVERITY_COLORS, SeverityChip } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { useTenant } from '../auth/useTenant';
import {
  absoluteTime,
  AiPanel,
  AssetContextPanel,
  dispositionLabel,
  namesEndpoint,
  outcomeLabel,
  sourceLabel,
  statusLabel,
  timeAgo,
  TraceView,
} from '@bluesphere/mdr-alert-panel';
import { PageNav } from '../components/PageNav';
import {
  ErrorState,
  GroupBadge,
  Loading,
  SelectClientPrompt,
  SourceBadge,
} from '../components/states';

export function DetectionDetail({ detectionId }: { detectionId: string }) {
  const { tenantId } = useSelectedTenant();
  const idToken = useAccessToken();
  const { can } = useTenant();
  // Enrichment is a `triage` request (Story 22 matrix), held on this Client.
  const canRequest = Boolean(tenantId && can('triage', tenantId));
  const detailKey = ['detection', tenantId, detectionId];

  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => getDetection(tenantId as string, detectionId, idToken),
    enabled: Boolean(tenantId && idToken),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });

  if (!tenantId) return <SelectClientPrompt what="Detections" />;

  if (detail.isPending) {
    return (
      <div className="bs-card">
        <Loading height="h-64" />
      </div>
    );
  }
  if (detail.isError) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return (
        <div className="bs-card p-16 text-center">
          <p className="text-sm font-semibold text-bluesphere-textOnLight">Detection not found</p>
          <p className="mt-1 text-sm text-bluesphere-muted">
            It may belong to a different Client than the one selected, or the link is wrong.
          </p>
        </div>
      );
    }
    return (
      <ErrorState
        title="Could not load this detection"
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const { detection: d, ai, trace, alert } = detail.data;
  const severityColor = SEVERITY_COLORS[d.severity];

  return (
    <div className="space-y-6">
      <PageNav backTo="/detections" backLabel="Detections" />
      <div className="bs-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <SeverityChip severity={d.severity} />
              <SourceBadge label={sourceLabel(d.source)} />
              {d.detection_name && <SourceBadge label={d.detection_name} />}
              <span className="text-xs text-bluesphere-muted" title={d.first_seen}>
                first seen {timeAgo(d.first_seen)}
              </span>
              {d.backfill && <span className="text-xs text-bluesphere-muted">· back-fill</span>}
            </div>
            <h1 className="text-xl font-bold lg:text-2xl">{d.title}</h1>
            {d.asset_name && <p className="mt-1 text-sm text-bluesphere-muted">{d.asset_name}</p>}
            {d.asset_join && (
              <p className="mt-0.5 text-xs text-bluesphere-muted">
                Same device as {sourceLabel(d.asset_join.source)}&rsquo;s {d.asset_join.display_name}{' '}
                (matched by {d.asset_join.by}
                {d.asset_join.candidates > 1 ? `, newest of ${d.asset_join.candidates}` : ''})
              </p>
            )}
            {(d.asset_groups ?? []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(d.asset_groups ?? []).map((g) => (
                  <GroupBadge key={g} slug={g} />
                ))}
              </div>
            )}
          </div>
          <div className="shrink-0 text-right">
            <div
              className="text-4xl font-bold leading-none tabular-nums"
              style={{ color: severityColor }}
            >
              {d.score}
            </div>
            <div className="mt-1 text-xs text-bluesphere-muted">score</div>
          </div>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-bluesphere-border pt-4 text-sm md:grid-cols-4">
          <Field
            label="Outcome"
            value={
              alert ? (
                <>
                  Raised →{' '}
                  <Link
                    to="/alerts/$alertRef"
                    params={{ alertRef: alert.short_id }}
                    className="font-mono font-semibold text-bluesphere-primary hover:underline"
                  >
                    {alert.short_id}
                  </Link>
                </>
              ) : (
                outcomeLabel(d.outcome)
              )
            }
          />
          <Field
            label="Alert status"
            value={
              alert
                ? `${statusLabel(alert.status)} · ${dispositionLabel(alert.disposition)}`
                : 'No alert'
            }
          />
          <Field
            label="Provider status"
            value={d.provider_status === 'closed' ? 'Closed by provider' : 'Open at provider'}
          />
          <Field label="Provider updated" value={absoluteTime(d.provider_updated_at)} />
        </dl>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Panel
            title="AI analysis"
            footer="AI-generated content is advisory and never decides. It sits beside the provider evidence, never instead of it."
          >
            <AiPanel
              tenantId={tenantId}
              detectionId={d.detection_id}
              ai={ai}
              canRequest={canRequest}
              refreshKey={detailKey}
            />
          </Panel>

          <Panel title="Provider evidence">
            <ProviderEvidence detection={d} />
            <ProviderPayload tenantId={tenantId} detectionId={d.detection_id} />
          </Panel>
        </div>

        <div className="space-y-6 lg:col-span-1">
          <Panel
            title="Decision"
            footer="Deterministic: the playbook's rules in order, first match wins. No model was consulted."
          >
            <TraceView trace={trace} />
          </Panel>

          {namesEndpoint(d) && (
            <AssetContextPanel tenantId={tenantId} asset={d.asset_name as string} />
          )}

          {alert && (
            <Panel title="Alert">
              <p className="text-sm">
                This detection is{' '}
                {alert.member_count === 1
                  ? 'the only member'
                  : `one of ${alert.member_count} members`}{' '}
                of{' '}
                <Link
                  to="/alerts/$alertRef"
                  params={{ alertRef: alert.short_id }}
                  className="font-mono font-semibold text-bluesphere-primary hover:underline"
                >
                  {alert.short_id}
                </Link>
                . Triage, the timeline and timing are there.
              </p>
              {d.incident_id && (
                <p className="mt-3 text-xs">
                  <Link to="/detections" search={{ incident: d.incident_id }} className="underline">
                    Other detections in this provider incident
                  </Link>
                </p>
              )}
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Provider evidence, per source ----------

function ProviderEvidence({ detection: d }: { detection: Detection }) {
  const rows: Array<[string, ReactNode]> =
    d.source === 'sentinelone'
      ? [
          ['Threat', d.title],
          ['Classification', d.detection_name ?? '—'],
          ['Analyst determination', d.provider_determination ?? 'undefined'],
          ['Incident status', d.provider_status === 'closed' ? 'resolved' : 'unresolved'],
          ['Storyline', d.incident_id ? <span className="font-mono">{d.incident_id}</span> : '—'],
          ['Endpoint', d.asset_name ?? '—'],
          ['Threat id', <span className="font-mono">{d.provider_detection_id}</span>],
        ]
      : [
          ['Title', d.title],
          ['Category', d.detection_name ?? '—'],
          ['Classification', d.provider_determination ?? '—'],
          ['Alert status', d.provider_status === 'closed' ? 'resolved' : 'open'],
          ['Incident', d.incident_id ? <span className="font-mono">{d.incident_id}</span> : '—'],
          ['Device / user', d.asset_name ?? '—'],
          ['Alert id', <span className="font-mono">{d.provider_detection_id}</span>],
        ];

  return (
    <div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <Field key={label} label={label} value={value} />
        ))}
        <Field label="Last seen" value={absoluteTime(d.last_seen)} />
        <Field
          label="MITRE ATT&CK"
          value={
            d.mitre.length ? (
              <span className="flex flex-wrap gap-1">
                {d.mitre.map((t) => (
                  <SourceBadge key={t} label={t} />
                ))}
              </span>
            ) : (
              '—'
            )
          }
        />
      </dl>
      <p className="mt-3 text-xs text-bluesphere-muted">
        These are the fields the pipeline normalizes. The provider's full payload is retained
        verbatim in the data lake and can be opened below.
      </p>
    </div>
  );
}

/**
 * The provider's full payload (task 67): fetched on click only, never with
 * the detail, so the lake read costs nothing until someone wants it.
 */
function ProviderPayload({ tenantId, detectionId }: { tenantId: string; detectionId: string }) {
  const idToken = useAccessToken();
  const [open, setOpen] = useState(false);
  const payload = useQuery({
    queryKey: ['detection-payload', tenantId, detectionId],
    queryFn: () => getDetectionPayload(tenantId, detectionId, idToken),
    enabled: open && Boolean(idToken),
    staleTime: Infinity,
    retry: false,
  });

  const notRetained = payload.error instanceof ApiError && payload.error.status === 404;
  const text = payload.data
    ? payload.data.payload_text ?? JSON.stringify(payload.data.payload, null, 2)
    : '';

  return (
    <div className="mt-4 border-t border-bluesphere-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PillButton
          variant="outline"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? 'Hide full provider payload' : 'Show full provider payload'}
        </PillButton>
        {open && payload.data && (
          <span className="text-xs text-bluesphere-muted">
            provider version {absoluteTime(payload.data.provider_updated_at ?? '')} · read{' '}
            {timeAgo(payload.data.retrieved_at)}
          </span>
        )}
      </div>
      {open && payload.isPending && <Loading height="h-24" />}
      {open && payload.isError && notRetained && (
        <p className="mt-3 text-sm text-bluesphere-muted">
          No raw payload was retained for this detection. It landed before the lake path existed,
          or the object has since expired.
        </p>
      )}
      {open && payload.isError && !notRetained && (
        <div className="mt-3">
          <ErrorState
            title="Could not load the provider payload"
            error={payload.error}
            onRetry={() => void payload.refetch()}
          />
        </div>
      )}
      {open && payload.data && (
        <pre className="bs-scroll mt-3 max-h-[480px] overflow-auto rounded-cardInner bg-bluesphere-surfaceMuted p-3 font-mono text-xs leading-relaxed text-bluesphere-textOnLight">
          {text}
        </pre>
      )}
    </div>
  );
}

// ---------- Small pieces ----------

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wider text-bluesphere-muted">{label}</dt>
      <dd className="truncate text-bluesphere-textOnLight">{value}</dd>
    </div>
  );
}

function Panel({
  title,
  footer,
  children,
}: {
  title: string;
  footer?: string;
  children: ReactNode;
}) {
  return (
    <section className="bs-card p-5">
      <h2 className="mb-3 text-xl font-bold lg:text-2xl">{title}</h2>
      {children}
      {footer && <p className="mt-3 text-[11px] text-bluesphere-muted">{footer}</p>}
    </section>
  );
}
